<script lang="ts" setup>
import {
  Barrier,
  Behavior,
  BPTAnswer,
  Co2ScanInterest,
  ExpertiseLevel,
  Motivation,
  SelfReliance,
  Target,
} from '@/models/bpt.model';
import { PropType, ref, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: Object as PropType<BPTAnswer[]>,
    required: true,
  },
  questionType: {
    type: String,
    required: true,
  },
});

const questionTypeAnswerValueMap: { [index: string]: string[] } = {
  interests: [],
  expertise: [...Object.values(ExpertiseLevel)],
  motivation: [
    ...Object.values(Motivation).filter((v) => v !== Motivation.Unknown),
  ],
  behavior: [...Object.values(Behavior)],
  barrier: [...Object.values(Barrier).filter((v) => v !== Barrier.Unknown)],
  target: [...Object.values(Target).filter((v) => v !== Target.Unknown)],
  'self-reliance': [...Object.values(SelfReliance)],
  'co2-scan': [
    ...Object.values(Co2ScanInterest).filter(
      (v) => v !== Co2ScanInterest.Unknown,
    ),
  ],
  location: [],
};

const answers = ref<BPTAnswer[]>([
  ...(props.modelValue || []).sort((a, b) => a.index - b.index),
]);

const emit = defineEmits(['update:modelValue', 'change']);

function update() {
  emit('update:modelValue', answers.value);
  emit('change');
}

function addAnswer() {
  answers.value.push({
    text: '',
    hint: '',
    value: '',
    index: answers.value.length,
  });
  update();
}

function removeAnswer(index: number) {
  answers.value.splice(index, 1);
  update();
}

function moveUp(answer: BPTAnswer) {
  const index = answers.value.findIndex((a) => a.id === answer.id);

  answers.value = [
    ...answers.value.slice(0, index - 1),
    answer,
    answers.value[index - 1],
    ...answers.value.slice(index + 1),
  ].map((v, index) => ({ ...v, index }));

  update();
}

function moveDown(answer: BPTAnswer) {
  const index = answers.value.findIndex((a) => a.id === answer.id);

  answers.value = [
    ...answers.value.slice(0, index),
    answers.value[index + 1],
    answer,
    ...answers.value.slice(index + 2),
  ].map((v, index) => ({ ...v, index }));

  update();
}

watch(
  () => props.modelValue,
  () => {
    Object.assign(answers.value, props.modelValue);
  },
  { deep: true },
);
</script>
<template>
  <div>
    <h3>Answers</h3>
    <v-alert
      type="info"
      v-if="
        (questionType !== 'location' &&
          questionType !== 'interests' &&
          answers.length !==
            questionTypeAnswerValueMap[questionType]?.length) ||
        0
      "
      >Please provide all answer options</v-alert
    >
    <v-list>
      <v-list-item v-for="(answer, index) in answers" :key="index">
        <div class="answer">
          <v-text-field
            v-model="answer.text"
            label="Answer"
            width="45%"
            :placeholder="`Answer ${index + 1}`"
            required
            @change="update"
          ></v-text-field>

          &nbsp;
          <v-btn
            icon="mdi-arrow-up"
            class="remove-button"
            density="compact"
            :disabled="index === 0 || answer.id === undefined"
            @click.prevent="moveUp(answer)"
          ></v-btn>
          &nbsp;
          <v-btn
            icon="mdi-arrow-down"
            class="remove-button"
            density="compact"
            :disabled="index === answers.length - 1 || answer.id === undefined"
            @click.prevent="moveDown(answer)"
          ></v-btn>
          &nbsp;
          <v-btn
            icon="mdi-delete-outline"
            class="remove-button"
            density="compact"
            @click.prevent="removeAnswer(index)"
          ></v-btn>
        </div>
        <div class="answer">
          <v-text-field
            width="60%"
            v-model="answer.hint"
            label="Hint"
            @change="update"
          ></v-text-field>
          &nbsp;
          <v-select
            width="40%"
            v-model="answer.value"
            label="Value"
            @change="update"
            :items="questionTypeAnswerValueMap[questionType]"
          ></v-select>
        </div>
      </v-list-item>
    </v-list>
    <v-btn @click="addAnswer" color="success">Add answer</v-btn>
  </div>
</template>
<style scoped>
.answer {
  display: flex;
  flex-direction: row;
  padding-right: 16px;
}
.remove-button {
  position: relative;
  top: 10px;
  left: 8px;
}

.calculation {
  background-color: rgb(179, 227, 255);
  padding: 16px;
  border-radius: 16px;
  margin: 16px 0;
  box-shadow: 2px 2px 4px silver;
}
.calculation-field {
  margin-bottom: 16px;
}
</style>
